import { DangerButton, PrimaryButton } from '@shared/components/buttons';
import { useContext, useEffect, useRef, useState } from 'react';
import { AlertContext } from '../../../contexts/alert-context';
import { PRE_CONFIRM_STATES } from '../../../data/receiving';
import { useAsyncAction } from '../../../hooks/use-async-action';
import type { ReceivingService } from '../../../services/receiving';
import { StepHeading } from './step-heading';

// On a refused Resume the screen itself doesn't change (the session is
// unchanged), and the near-instant Resume→Checking→Resume button flip is easy
// to miss — so raise a global toast (the same alerts surface used elsewhere)
// so it's unmistakable that the click registered and the file is still busy.
const RESUME_REFUSED_NOTICE_MS = 10_000;

// Cancel is shown only when paused_from is pre-Confirm (PRE_CONFIRM_STATES,
// imported from data/receiving — the same set canCancelSession derives from).
// Post-Confirm (sending_to_lpos) means the write was already in flight: Cancel
// is forbidden per Invariant 5; only Abandon is offered.
const THIRTY_MIN_MS = 30 * 60 * 1000;

type PausedLposBusyProps = {
  storeId: string;
  sessionId: string;
  service: ReceivingService;
  pausedFrom: string | undefined;
  /** Server timestamp the pause began. Always set by the backend in this state;
   * when absent we simply don't show the elapsed-time reminder (never synthesize one). */
  pausedSince: string | undefined;
  readOnly: boolean;
  /** Monotonic count of refused Resume probes; each increment raises a fresh
   * "still in use" toast (so back-to-back refusals each get feedback). */
  probeRefusedCount?: number;
  /** Monotonic count of busy-refused Abandon clears (companion replied
   * rcv_clear_result{busy:true} — LPOS holds the file, session kept alive).
   * Non-zero shows the inline "close LiquorPOS, then Abandon again" message;
   * each increment also raises a toast. */
  abandonBusyCount?: number;
};

/**
 * Pause screen (spec §10 — paused_lpos_busy).
 *
 * Cause-neutral — the holder may be LiquorPOS running its own receiving, an
 * antivirus tool, or a search indexer; the resolution is the same: finish
 * (or close) whatever holds the file, then click Resume.
 *
 * The secondary action is Cancel XOR Abandon, depending on paused_from:
 *   - pre-Confirm (vendor/invoices/review): we never wrote the file — the holder
 *     is LiquorPOS's own work, not ours. Cancel cleanly discards this receiving;
 *     Abandon's file-clear would be a no-op here (authorship-gated), so it's only
 *     confusing. Show Cancel.
 *   - sending_to_lpos (post-Confirm): the write was already in flight, so Cancel
 *     is forbidden (Invariant 5) and Abandon is the safe exit (it clears any bytes
 *     we authored before ending the session). Show Abandon.
 *
 * 30-min reminder: a gentle note beneath the buttons after 30 minutes have
 * elapsed since paused_since, pointing at whichever exit is shown.
 */
export function PausedLposBusy({
  storeId,
  sessionId,
  service,
  pausedFrom,
  pausedSince,
  readOnly,
  probeRefusedCount = 0,
  abandonBusyCount = 0,
}: PausedLposBusyProps) {
  const { addWarningAlert } = useContext(AlertContext);
  // Inline error from a failed Resume/Abandon request (e.g. the 409 "companion
  // is not connected") — without it the button silently re-enables and the user
  // can't tell their click did nothing. Shared across Resume and Abandon: each
  // clears it on start, so only the most recent action's error is shown.
  const [actionError, setActionError] = useState<string | null>(null);
  const { pending: resuming, run: handleResume } = useAsyncAction(
    () => service.resume(storeId, sessionId),
    { errorLabel: 'receiving: resume failed', setError: setActionError, disabled: readOnly }
  );
  const { pending: abandoning, run: handleAbandon } = useAsyncAction(
    () => service.abandon(storeId, sessionId),
    { errorLabel: 'receiving: abandon failed', setError: setActionError, disabled: readOnly }
  );

  const canCancel = pausedFrom != null && PRE_CONFIRM_STATES.has(pausedFrom);
  const showReminder =
    pausedSince != null && Date.now() - new Date(pausedSince).getTime() >= THIRTY_MIN_MS;

  // Toast on every refused Resume probe — the screen otherwise only flips the
  // button back, which is easy to miss. Fire on each increment (not just the
  // first) so a second click against a still-busy file gives feedback too.
  const seenRefusals = useRef(probeRefusedCount);
  useEffect(() => {
    if (probeRefusedCount > seenRefusals.current) {
      addWarningAlert('LiquorPOS receiving still in progress', {
        details: 'Finish any receiving in LiquorPOS, then click Resume again.',
        autoCloseAfterTimeMS: RESUME_REFUSED_NOTICE_MS,
      });
    }

    seenRefusals.current = probeRefusedCount;
  }, [probeRefusedCount, addWarningAlert]);

  // Toast on each busy-refused Abandon clear (mirror of the Resume toast above);
  // the persistent inline message below carries the instruction.
  const seenAbandonBusy = useRef(abandonBusyCount);
  useEffect(() => {
    if (abandonBusyCount > seenAbandonBusy.current) {
      addWarningAlert('LiquorPOS is still using the receiving file', {
        details: 'Close LiquorPOS completely, then click Abandon again.',
        autoCloseAfterTimeMS: RESUME_REFUSED_NOTICE_MS,
      });
    }

    seenAbandonBusy.current = abandonBusyCount;
  }, [abandonBusyCount, addWarningAlert]);

  return (
    <div className="flex flex-col gap-4" data-testid="paused-lpos-busy">
      <StepHeading>The receiving file is in use.</StepHeading>
      <p className="text-base leading-relaxed text-gray-700">
        Finish any receiving in LiquorPOS, then click Resume. If LiquorPOS is not running a
        receiving, close it completely and click Resume.
      </p>

      {abandonBusyCount > 0 && (
        <p className="text-sm font-medium text-amber-800" data-testid="pause-abandon-busy">
          LiquorPOS is still using the receiving file. Close LiquorPOS completely, then click
          Abandon again.
        </p>
      )}
      {actionError && (
        <p className="text-sm font-medium text-red-700" data-testid="pause-action-error">
          {actionError}
        </p>
      )}
      {showReminder && (
        <p className="text-sm text-gray-600" data-testid="pause-reminder">
          {canCancel
            ? 'Still stuck? Use Cancel session at the top of the page to discard this and start over.'
            : 'Still stuck? Click Abandon to clear this and start over. LiquorPOS may already have imported this. Check LiquorPOS before starting over.'}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 mt-2">
        {!canCancel && (
          <DangerButton onClick={handleAbandon} disabled={readOnly || resuming || abandoning}>
            {abandoning ? 'Abandoning…' : 'Abandon'}
          </DangerButton>
        )}
        <PrimaryButton onClick={handleResume} disabled={readOnly || resuming || abandoning}>
          {resuming ? 'Checking…' : 'Resume'}
        </PrimaryButton>
      </div>
    </div>
  );
}
